import { z } from "zod";

import {
  cardGridSectionSchema,
  checklistSectionSchema,
  ctaBannerSectionSchema,
  faqSectionSchema,
  heroSectionSchema,
  pageSeoSchema,
  profileSectionSchema,
} from "@/content/schemas";
import { siteConfig } from "@/content/site";
import { validateContent } from "@/content/validateContent";

const workshopPageSchema = z.object({
  seo: pageSeoSchema,
  hero: heroSectionSchema,
  audienceSection: cardGridSectionSchema.extend({
    accentLine: z.string().optional(),
  }),
  problemSection: z.object({
    title: z.string().min(1),
    paragraphs: z.array(z.string().min(1)).min(1),
    highlight: z.string().optional(),
  }),
  outcomesSection: checklistSectionSchema,
  curriculumSection: cardGridSectionSchema,
  formatSection: cardGridSectionSchema.extend({
    accentLine: z.string().optional(),
  }),
  examplesSection: cardGridSectionSchema,
  instructorSection: profileSectionSchema,
  pricingSection: z.object({
    title: z.string().min(1),
    badge: z.string().min(1),
    items: z.array(z.string().min(1)).min(1),
    primaryCta: z.object({
      label: z.string().min(1),
      href: z.string().min(1),
      external: z.boolean(),
    }),
    footerNote: z.string().optional(),
  }),
  faqSection: faqSectionSchema,
  finalCta: ctaBannerSectionSchema,
});

export const workshopPageContent = validateContent("workshopPage", workshopPageSchema, {
  seo: {
    title: "Workshop Updates | Tech Savvy Starts Here",
    description:
      "There is no live workshop open right now. Join the Tech Savvy Starts Here newsletter to hear when the next practical workshop goes live.",
    canonicalPath: "/workshops",
    socialImage: "/social-card.svg",
  },
  hero: {
    eyebrow: "Workshop Updates",
    title: "No live workshop is open right now",
    description:
      "The live workshop program is currently between cohorts. The next session will be announced when the date, topic, and registration details are ready.",
    secondaryDescription:
      "You can still explore what the website-building workshop covers and join the newsletter to be first in line when registration opens again.",
    detailRows: [
      { label: "Status", value: "No current live workshop" },
      { label: "Next date", value: "To be announced" },
    ],
    primaryCta: {
      label: "Get Workshop Updates",
      href: "/newsletter",
      external: false,
    },
    secondaryCta: {
      label: "See What You’ll Learn",
      href: "/workshops#outcomes",
      external: false,
    },
    background: "gradient",
    media: {
      type: "image",
      imageSrc: "/workshop-image.png",
      imageAlt: "Woman building a website on a laptop in a modern workspace",
    },
  },
  audienceSection: {
    title: "Who This Workshop Is For",
    cards: [
      {
        icon: "Briefcase",
        title: "Small business owners",
        description: "Who need a simple website for a service or business.",
      },
      {
        icon: "Mic",
        title: "Coaches and consultants",
        description: "Who want a clear website to explain what they do.",
      },
      {
        icon: "GraduationCap",
        title: "Creators and educators",
        description: "Who are selling a class, event, or digital product.",
      },
      {
        icon: "Rocket",
        title: "Beginners",
        description: "Who want to launch online without learning to code first.",
      },
    ],
    accentLine: "You do not need technical experience. You need a real business and a willingness to build one practical website.",
  },
  problemSection: {
    title: "Why many people stay stuck online",
    paragraphs: [
      "A lot of people think they need a full website before they can launch anything. Others get blocked by design, coding, or tool overwhelm.",
      "In many cases, what they need first is a simple website that explains the business and tells people what to do next.",
    ],
    highlight: "This workshop helps you use AI to build that first website faster and with more confidence.",
  },
  outcomesSection: {
    title: "What you will walk away with",
    items: [
      "A simple website for your business",
      "A clear website structure you can reuse later",
      "AI prompts to help with writing and website building",
      "A practical launch checklist",
      "More confidence using AI to create business assets",
    ],
    visualCards: [
      { icon: "FileText", title: "Website", description: "Clear business website", tone: "default" },
      { icon: "Layout", title: "Website structure", description: "Reusable layout", tone: "soft" },
      { icon: "Wand2", title: "AI prompts", description: "Guided writing prompts", tone: "soft" },
      { icon: "CheckSquare", title: "Launch checklist", description: "Practical next steps", tone: "default" },
    ],
  },
  curriculumSection: {
    title: "What we will cover",
    cards: [
      {
        icon: "Target",
        title: "Plan your website goal",
        description: "Define the goal of the website and what action you want visitors to take.",
        kicker: "Part 1",
      },
      {
        icon: "FileText",
        title: "Write with AI support",
        description: "Use AI to shape your headline, website sections, and CTA with clarity.",
        kicker: "Part 2",
      },
      {
        icon: "Layout",
        title: "Build the website",
        description: "Use a beginner-friendly workflow to turn your website text into a usable website.",
        kicker: "Part 3",
      },
      {
        icon: "Sparkles",
        title: "Polish and launch",
        description: "Refine the website, check your structure, and get ready to publish or share it.",
        kicker: "Part 4",
      },
    ],
  },
  formatSection: {
    title: "How the workshop works",
    cards: [
      { icon: "Video", title: "Live workshop", description: "Attend live and build in real time." },
      { icon: "BookOpen", title: "Step-by-step guidance", description: "Follow a clear guided structure." },
      { icon: "Users", title: "Beginner friendly", description: "No coding knowledge required." },
      { icon: "Lightbulb", title: "Practical examples", description: "Learn through real use cases." },
      { icon: "PlayCircle", title: "Replay access", description: "Review the session after it ends." },
      { icon: "Gift", title: "Templates and prompts", description: "Leave with assets you can reuse." },
    ],
    accentLine: "You will build as you learn, not just watch and take notes.",
  },
  examplesSection: {
    title: "Examples of what you can build",
    cards: [
      { icon: "Briefcase", title: "Service website", description: "Showcase your professional services." },
      { icon: "Calendar", title: "Event registration", description: "Collect signups for an event." },
      { icon: "Mic", title: "Coaching business", description: "Present a coaching or consulting business." },
      { icon: "ShoppingBag", title: "Digital product website", description: "Sell a class, course, or download." },
    ],
  },
  instructorSection: {
    title: "Meet Dr. Ijeoma Ndu",
    name: "Dr. Ijeoma Ndu",
    role: "ML Engineer and AI Educator",
    description:
      "Dr. Ijeoma Ndu helps people use AI and digital tools in practical ways. She is a former lecturer, a machine learning engineer, and the current CTO of a health tech startup. Through Tech Savvy Starts Here, she teaches with a clear, beginner friendly approach focused on practical use, clarity, and confidence.",
    icon: "User",
    imageSrc: siteConfig.headshotImage,
    imageAlt: "Dr. Ijeoma Ndu headshot",
  },
  pricingSection: {
    title: "Get notified when registration opens",
    badge: "Registration currently closed",
    items: [
      "No live workshop is available for purchase right now",
      "New dates will be shared through the newsletter",
      "Future sessions may include replay access, templates, prompts, and launch guidance",
      "Business and group training inquiries are welcome",
    ],
    primaryCta: {
      label: "Subscribe for Updates",
      href: "/newsletter",
      external: false,
    },
    footerNote: "For private training or partnership inquiries, use the contact page.",
  },
  faqSection: {
    title: "Frequently asked questions",
    items: [
      {
        question: "Do I need coding experience?",
        answer: "No. The workshop is designed for beginners and does not require coding experience.",
      },
      {
        question: "Do I need to already have a business?",
        answer: "No, but it helps to have a business, service, event, or product you want the website to support.",
      },
      {
        question: "Will there be a replay?",
        answer: "Replay details will be shared when the next live workshop is announced.",
      },
      {
        question: "What tools will we use?",
        answer:
          "The workflow is designed around practical, beginner-friendly AI and website-building tools. You will need a laptop or mobile device to follow along during the session but a laptop is preferred.",
      },
      {
        question: "Is this the only live paid workshop right now?",
        answer: "No live paid workshop is currently open for registration.",
      },
    ],
  },
  finalCta: {
    title: "Be ready for the next workshop",
    description:
      "Join the newsletter for the next live workshop date, practical AI lessons, and beginner friendly digital skills resources.",
    primaryCta: {
      label: "Join the Newsletter",
      href: "/newsletter",
      external: false,
    },
    secondaryCta: {
      label: "Contact for Training",
      href: "/contact",
      external: false,
    },
    tone: "dark",
  },
});
